import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, MessageCircle, ShieldCheck, Plane, Building2, Sparkles, Clock3 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const COPY = {
  en: {
    eyebrow: "Frequently Asked Questions",
    title: "Questions international travelers ask before booking a chauffeur in Jeddah",
    intro:
      "This FAQ helps international travelers understand how BlackRide handles airport transfers, executive travel, Umrah transport, intercity rides, and premium chauffeur bookings in Saudi Arabia.",
    faqs: [
      {
        question: "How is BlackRide different from regular ride-hailing in Jeddah?",
        answer:
          "BlackRide is designed for travelers who want a more premium, predictable, and private experience than standard ride-hailing. That includes professional chauffeurs, cleaner presentation, premium vehicle classes, and smoother airport and hotel coordination.",
        icon: ShieldCheck,
      },
      {
        question: "Can international travelers pre-book airport transfer in Jeddah before arrival?",
        answer:
          "Yes. Many guests book before landing so their airport transfer is arranged in advance. This is especially useful for international arrivals, families, executives, and guests visiting Saudi Arabia for the first time.",
        icon: Plane,
      },
      {
        question: "Is BlackRide suitable for business travel and VIP guests in Saudi Arabia?",
        answer:
          "Yes. Our service is designed for executives, hotel guests, concierge referrals, and VIP travelers who need punctual, polished, and comfortable transportation in Jeddah and beyond.",
        icon: Building2,
      },
      {
        question: "Do you provide premium vehicles such as BMW 7 Series and other luxury options?",
        answer:
          "Yes. BlackRide offers premium sedans, SUVs, executive vans, and ultra-premium options for travelers who want a more refined chauffeur experience in Saudi Arabia.",
        icon: Sparkles,
      },
      {
        question: "Can I use BlackRide for Hajj, Umrah, hotel transfers, and intercity trips?",
        answer:
          "Yes. Guests regularly book BlackRide for Jeddah airport transfers, Makkah and Madinah connections, hotel transfers, business meetings, and premium religious travel itineraries.",
        icon: MessageCircle,
      },
      {
        question: "How fast can I confirm my chauffeur booking?",
        answer:
          "Bookings can be confirmed quickly through WhatsApp once your pickup, destination, date, time, and vehicle preference are shared. This makes the process simple for international travelers who want direct communication and clear confirmation.",
        icon: Clock3,
      },
    ],
  },
  ar: {
    eyebrow: "الأسئلة الشائعة",
    title: "إجابات مهمة للمسافرين الدوليين قبل حجز خدمة السائق في جدة",
    intro:
      "هذا القسم يجيب عن أكثر الأسئلة شيوعاً حول النقل الفاخر من مطار جدة، الرحلات التنفيذية، رحلات العمرة، وخدمة السائق الخاص داخل جدة.",
    faqs: [
      {
        question: "ما الفرق بين بلاك رايد وخدمات طلب المركبات العادية في جدة؟",
        answer:
          "بلاك رايد مخصصة للمسافرين الذين يبحثون عن تجربة أكثر فخامة وخصوصية واعتمادية، مع سائقين محترفين، مركبات مميزة، وتنسيق أفضل لرحلات المطار والفنادق.",
        icon: ShieldCheck,
      },
      {
        question: "هل يمكن للمسافرين الدوليين حجز نقل مطار جدة مسبقاً قبل الوصول؟",
        answer:
          "نعم. كثير من الضيوف يفضلون حجز النقل مسبقاً قبل الوصول، خاصة القادمين من الخارج، العائلات، رجال الأعمال، والزوار لأول مرة إلى السعودية.",
        icon: Plane,
      },
      {
        question: "هل الخدمة مناسبة لسفر الأعمال وكبار الشخصيات في السعودية؟",
        answer:
          "نعم. تم تصميم الخدمة لتناسب التنفيذيين، ضيوف الفنادق، توصيات الكونسيرج، وكبار الشخصيات الذين يحتاجون إلى نقل أنيق ومريح ودقيق في المواعيد.",
        icon: Building2,
      },
      {
        question: "هل توفرون سيارات فاخرة مثل BMW الفئة السابعة وخيارات راقية أخرى؟",
        answer:
          "نعم. نوفر سيدان فاخرة، سيارات SUV مميزة، فان تنفيذي، وخيارات فائقة الفخامة للمسافرين الباحثين عن تجربة راقية في السعودية.",
        icon: Sparkles,
      },
      {
        question: "هل يمكن استخدام بلاك رايد لرحلات الحج والعمرة والتنقل بين المدن؟",
        answer:
          "نعم. يستخدم الضيوف الخدمة لانتقالات مطار جدة، التنقل إلى مكة والمدينة، رحلات الفنادق، الاجتماعات، والبرامج الدينية الراقية.",
        icon: MessageCircle,
      },
      {
        question: "كم يستغرق تأكيد الحجز؟",
        answer:
          "يمكن تأكيد الحجز بسرعة عبر واتساب بعد إرسال تفاصيل الانطلاق والوجهة والتاريخ والوقت وفئة المركبة المطلوبة.",
        icon: Clock3,
      },
    ],
  },
};

export default function FAQSection() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;
  const [openIndex, setOpenIndex] = useState(0);

  const faqSchema = useMemo(
    () => JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (COPY.en.faqs || []).map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    }),
    []
  );

  return (
    <section id="faq" data-testid="faq-section" className="relative py-20 md:py-24 bg-[#050505]">
      <script id="homepage-faq-schema" type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mb-14"
        >
          <p className="text-xs uppercase tracking-[0.25em] text-[#C7A239] font-medium mb-4">{copy.eyebrow}</p>
          <h2 className="font-[Playfair_Display] text-3xl md:text-4xl lg:text-5xl font-semibold text-white tracking-tight mb-5">
            {copy.title}
          </h2>
          <p className="text-base md:text-lg text-gray-400 leading-relaxed">{copy.intro}</p>
        </motion.div>

        <div className="space-y-4">
          {copy.faqs.map((item, index) => {
            const Icon = item.icon;
            const isOpen = index === openIndex;
            return (
              <motion.article
                key={item.question}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: index * 0.05 }}
                className="rounded-[28px] border border-white/8 bg-[#111] overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : index)}
                  className="w-full flex items-center gap-4 px-6 md:px-7 py-5 text-left"
                  aria-expanded={isOpen}
                >
                  <div className="w-11 h-11 rounded-2xl bg-[#C7A239]/10 flex items-center justify-center shrink-0">
                    <Icon className="w-5 h-5 text-[#C7A239]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl text-white font-medium leading-snug">{item.question}</h3>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? "rotate-180" : "rotate-0"}`} />
                </button>
                <AnimatePresence initial={false}>
                  {isOpen ? (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="px-6 md:px-7 pb-6 text-sm md:text-[15px] text-gray-400 leading-7 border-t border-white/6">
                        <div className="pt-5">{item.answer}</div>
                      </div>
                    </motion.div>
                  ) : null}
                </AnimatePresence>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
